import { gql, useQuery } from '@apollo/client';
import { getCountryAbbreviationFromCustomsBrokerId } from '@xbcb/core';
import {
  DateSearchOperator,
  KeywordSearchOperator,
  UsCbpEntryReleaseStatus,
  UsCbpEntrySummaryStatus,
} from '__generated__/graphql';
import { Card, Statistic, Button } from 'antd';
import { ArrowUpOutlined } from 'icons';
import { useCurrentUser } from 'libs/hooks';
import { useState, useEffect, useRef } from 'react';
import { usePageVisibility } from 'react-page-visibility';
import { activeBrokerLocalStorageKey } from 'reducers/activeBroker';
import { UiStage } from '@xbcb/ui-types';
import { getEnv } from '@xbcb/ui-env';

const entryCreatedCutoffTime = '2020-01-01';

const combinedEntryQuery = gql`
  query combinedEntryFiledData(
    $usEntryInput: SearchUsConsumptionEntryInput!
    $usType86Input: SearchUsType86EntryInput!
  ) {
    searchUsConsumptionEntries(input: $usEntryInput) {
      totalResults
    }
    searchUsType86Entries(input: $usType86Input) {
      totalResults
    }
  }
`;

// Launch button should only be shown to Bryce and Ankit in production
const authorizedUsers = {
  // grnikhil
  [UiStage.LOCAL]: [
    'operatorUser_0d6eceb2-1d0e-4601-a6e7-af8f6e7f70c7',
    'operatorUser_a6855271-e803-48e0-8642-312cbd7380fe',
  ],
  [UiStage.ALPHA]: [
    'operatorUser_408648c9-fa0d-45bf-a96a-cbd366cd9ba9',
    'operatorUser_0d6eceb2-1d0e-4601-a6e7-af8f6e7f70c7',
    'operatorUser_26c7780e-a8be-4507-a45a-2faf2a0c01a7',
  ],
  [UiStage.BETA]: ['operatorUser_26c7780e-a8be-4507-a45a-2faf2a0c01a7'],
  [UiStage.GAMMA]: [
    'operatorUser_0d6eceb2-1d0e-4601-a6e7-af8f6e7f70c7',
    'operatorUser_a6855271-e803-48e0-8642-312cbd7380fe',
  ],
  // aankit (Ankit) & mcdermot (Bryce) and grnikhil(for testing)
  [UiStage.PROD]: [
    'operatorUser_993d19c4-a474-4771-93d6-720f2c4d308d',
    'operatorUser_334547dc-c707-4866-a3f0-8c157f64dcf9',
    'operatorUser_b5e78539-b26f-4ed2-8118-e066216330d8',
  ],
};

export type EntryStatisticsProps = {
  isPageLoading?: boolean;
};

const EntryStatistics = ({
  isPageLoading,
}: EntryStatisticsProps): React.ReactElement => {
  const [showCard, setShowCard] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const isVisible = usePageVisibility();
  const prevValueRef = useRef(0);
  const currentUser = useCurrentUser();
  // July 16, 2024 12:00 PM CST ( All Hands end time, info given by Ankit )
  const launchDate = new Date('2025-07-16T17:00:00.000Z');
  const currentDate = new Date();
  const isLaunched = currentDate >= launchDate;
  const stage = getEnv().stage;

  const isAuthorized = authorizedUsers[stage].includes(currentUser?.id);

  const currentActiveBrokerId =
    localStorage.getItem(activeBrokerLocalStorageKey) ||
    currentUser?.customsBroker?.id;

  // fallback in case the broker is not found in local storage
  const getCountryFromLocale = () => {
    const locale = navigator.language || navigator.languages[0];
    // This gives you language-country code like "en-US"
    const countryCode = locale.split('-')[1];
    return countryCode; // Returns "US", "GB", etc.
  };

  const activeCountry = currentActiveBrokerId
    ? getCountryAbbreviationFromCustomsBrokerId(currentActiveBrokerId)
    : getCountryFromLocale();

  const handleButtonClick = () => {
    setShowCard(true);
  };

  /* only needed to show in US */
  const isFeatureEnabled = activeCountry === 'US';

  // Reduce network calls for geography where feature is not enabled
  // or the card is not launched
  const skipCombinedQuery =
    !currentUser?.id ||
    !isVisible ||
    !(showCard || isLaunched) ||
    !isFeatureEnabled;

  const { data, loading } = useQuery(combinedEntryQuery, {
    skip: skipCombinedQuery,
    variables: {
      usEntryInput: {
        searchCriteria: {
          entryNumber: { operator: KeywordSearchOperator.EXISTS },
          summaryStatus: {
            values: [UsCbpEntrySummaryStatus.FILED],
            operator: KeywordSearchOperator.ONE_OF,
          },
          createdTime: {
            value: entryCreatedCutoffTime,
            operator: DateSearchOperator.AFTER,
          },
          deletedTime: { operator: DateSearchOperator.DOES_NOT_EXIST },
        },
      },
      usType86Input: {
        searchCriteria: {
          entryNumber: { operator: KeywordSearchOperator.EXISTS },
          releaseStatus: {
            values: [UsCbpEntryReleaseStatus.RELEASED],
            operator: KeywordSearchOperator.ONE_OF,
          },
          createdTime: {
            value: entryCreatedCutoffTime,
            operator: DateSearchOperator.AFTER,
          },
          deletedTime: { operator: DateSearchOperator.DOES_NOT_EXIST },
        },
      },
    },
    pollInterval: 60000,
  });

  const usEntryFiled = data?.searchUsConsumptionEntries?.totalResults ?? 0;
  const type86EntryFiled = data?.searchUsType86Entries?.totalResults ?? 0;

  // There is a diff in value due to indexer issue where 68114 entries
  // (09/2020 - 06/2021) are not indexed on broker id.
  // WOA adds broker id on backend causing UI to reflect different filed entries.
  // Discussed with Ankit to add the diff manually in prod instead of reindexing
  const delta = stage === UiStage.PROD ? 68114 : 0;
  const totalEntriesFiled = usEntryFiled + type86EntryFiled + delta;

  useEffect(() => {
    if (totalEntriesFiled > prevValueRef.current && prevValueRef.current > 0) {
      setAnimationPhase(1);
      setTimeout(() => setAnimationPhase(2), 300);
      setTimeout(() => setAnimationPhase(3), 350);
      setTimeout(() => setAnimationPhase(0), 400);
    }
    prevValueRef.current = totalEntriesFiled;
  }, [totalEntriesFiled]);

  return (
    <>
      {!isPageLoading && isFeatureEnabled && currentUser?.id && (
        <div
          style={{
            marginTop: '4px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {!isLaunched && !showCard && isAuthorized && (
            <Button onClick={handleButtonClick} style={{ margin: '8px' }}>
              Show Total US Entries Filed
            </Button>
          )}
          {(isLaunched || showCard) && (
            <Card bordered={false} size="small">
              <Statistic
                title="Total US Entries Filed"
                loading={loading}
                value={totalEntriesFiled}
                valueStyle={{ color: '#3f8600' }}
                prefix={
                  <ArrowUpOutlined
                    style={{
                      transform:
                        animationPhase === 1
                          ? 'translateY(-20px)'
                          : animationPhase === 2
                          ? 'translateY(20px)'
                          : animationPhase === 3
                          ? 'translateY(20px)'
                          : 'translateY(0px)',
                      opacity:
                        animationPhase === 1 || animationPhase === 2 ? 0 : 1,
                      transition:
                        animationPhase === 2
                          ? 'none'
                          : animationPhase === 3
                          ? 'none'
                          : 'transform 0.3s ease-out, opacity 0.2s ease-out',
                    }}
                  />
                }
              />
            </Card>
          )}
        </div>
      )}
    </>
  );
};

export default EntryStatistics;
